import { useQuery } from "@tanstack/react-query";
import { DocumentApiV2 } from "../../../../services/document/document-v2.service";
import { TreeDataNode } from "antd";
import { DriveItem } from "@microsoft/microsoft-graph-types";
import { useMemo } from "react";
import { DirectoriesHelper } from "../../utils/DirectoriesHelper";
import moment from "moment";

const selectHandler = (response: any) => {
  if (Array.isArray(response)) return response.map((item: any) => item.driveItem);
  return response?.map((item: any) => item.driveItem!);
};

const directoryKeys = {
  all: ["folders"],
};

const sortTreeByDate = (data: any) => {
  return data
    .map((item: any) => {
      const newItem = { ...item };
      if (newItem.children) {
        newItem.children = sortTreeByDate(newItem.children);
      }
      return newItem;
    })
    .sort((a: any, b: any) => (moment(a.createdDateTime).isBefore(moment(b.createdDateTime)) ? -1 : 1));
};

export const useDocumentsFolders = () => {
  const query = useQuery({
    queryKey: directoryKeys.all,
    queryFn: () => DocumentApiV2.getDocumentsDirectory(),
    select: selectHandler,
  });

  const treeData: (TreeDataNode & DriveItem)[] = useMemo(() => {
    if (!query.data) return [];
    return DirectoriesHelper.buildTree(query.data, (item) => ({ ...item, key: item.id, title: item.name }));
  }, [query.data]);

  const treeDataSelect = useMemo(() => {
    if (!query.data) return [];
    return DirectoriesHelper.buildTree(query.data, (item) => ({ ...item, value: item.id, title: item.name }));
  }, [query.data]);

  const sortedTreeData = useMemo(() => sortTreeByDate(treeData), [treeData]);

  const getTreeDirectory = (itemId: string, directoryList: DriveItem[]) => {
    if (!directoryList || !itemId) return [];
    const map = new Map();
    for (const item of directoryList) {
      map.set(item.id, item);
    }
    const directoryNode = directoryList.find((item) => item.id === itemId);
    if (!directoryNode) return [];
    const directory = [directoryNode];
    let currentNode = directoryNode;
    while (currentNode) {
      const parentDirectoryNode = map.get(currentNode.parentReference?.id);
      if (parentDirectoryNode) {
        directory.push(parentDirectoryNode);
      }
      currentNode = parentDirectoryNode;
    }

    return directory.reverse();
  };

  return { ...query, treeData, sortedTreeData, treeDataSelect, getTreeDirectory };
};
